import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Button } from '@mui/material';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  acknowledgeTmsAlerts,
  fetchTmsAlerts,
  isImportantAlert,
  toErrorMessage,
  type TmsAlert,
} from './orderActionsApi';
import { ALERT_BUTTON_SX, OrderAlertCard } from './OrderAlertCard';
import { showVehicleSuggestions } from './vehicleSuggestionsAnchor';
import { useHasPermission } from '../PermissionGate';
import { useFeature } from '../featureFlags/FeatureGate';
import { asSentence, hedgeAsEstimate, splitOutsideParens, startsLowercase } from './alertText';

/**
 * Alert o przeplanowaniu — na górze karty zlecenia (status 29.09.2026:
 * „Alert o przeplanowaniu pojawia się, gdy sytuacja mocno się zmieni: korek,
 * wypadek, strajk, awaria albo odwołane zlecenie, które zwalnia lepsze
 * auto”).
 *
 * Okresowy przegląd obsadzonych zleceń (`apps/api/src/user-offers/
 * replan-alerts.ts`) zostawia wpis, gdy obsadzone auto nie zdąży albo nie
 * dowiezie, jego kierowca jest niedostępny, auto wypadło z ruchu albo
 * zwolniło się wyraźnie bliższe auto. To ostrzeżenie, nie automat: nic nie
 * zostało zmienione, a „Przyjmuję do wiadomości” (do 01.10.2026
 * „Potwierdzam, widziałem”) zapisuje tylko wpis w dzienniku. Potwierdzenie
 * idzie tą samą drogą co alerty z TMS, ale obejmuje wyłącznie alerty
 * pokazane w tej karcie.
 *
 * Omówienie 01.10.2026: nagłówek „Rekomendowane przeplanowanie” (wcześniej
 * „Do rozważenia przeplanowanie”). Przegląd przed prezentacją 02.10.2026:
 * wspólny wygląd z alertem zmian w TMS (`OrderAlertCard`), zastrzeżenie
 * o decyzji planisty raz — w stopce, nie przy każdym alercie — i drugi
 * przycisk, „Pokaż rekomendowane pojazdy”, który przewija do podpowiedzi
 * na tej karcie i liczy je, jeśli jeszcze nie są policzone.
 *
 * Decyzja użytkownika 12.10.2026 („B4 — wyróżnij te ważniejsze alerty”; na
 * produkcji tydzień dał 1331 alertów i zero potwierdzeń): alert z pewnym
 * powodem z kartoteki TMS (nieobecność kierowcy, pojazd poza eksploatacją,
 * niedostępny, wykluczony) jest ważny — karta czerwona, „Pilne
 * przeplanowanie”, alert rozwinięty na górze. Szacunki (spóźnienie, bliższy
 * pojazd, zajętość) są zwykłe: jedna linia „Inne podpowiedzi przeplanowania
 * (N)” z rozwinięciem, a gdy nic pilnego nie ma — cała karta stonowana.
 * Ważność liczy API (`apps/api/src/user-offers/alert-importance.ts`).
 * „Przyjmuję do wiadomości” bez zmian — obejmuje wszystkie alerty karty.
 *
 * Decyzja użytkownika 12.10.2026 (A2: „alert przeplanowania — opcja: widzi
 * tylko master”): alert proponuje zmianę obsadzenia, a obsadza i przepina
 * auto wyłącznie uprawnienie „Obsadzanie pojazdem” (`FTL_ASSIGN` — planista
 * główny, administratorzy, użytkownik). Bez niego karty nie ma i nie pytamy
 * o alerty; rozstrzyga API (bez uprawnienia nie oddaje przeplanowania,
 * a potwierdzenie kończy się 403) — tu tylko odbicie.
 */

/** Dymek „Skąd ten alert?” jak na produkcji. */
const REPLAN_ALERT_ORIGIN_BASE =
  'Co 30 minut system sprawdza obsadzone zlecenia z załadunkiem w ciągu najbliższych 48 h. ' +
  'Alert pojawia się, gdy obsadzony pojazd nie zdąży na załadunek albo nie dowiezie ładunku ' +
  'w oknie rozładunku (szacunek, tylko przy aktualnej pozycji z GPS), gdy kierowca jest ' +
  'niedostępny, pojazd jest poza eksploatacją albo zajęty w tym samym czasie innym zleceniem, ' +
  'a także gdy wyraźnie bliżej jest wolny pojazd.';
/** Dopisek o ważności (przełącznik `importantAlerts`, 12.10.2026). */
const REPLAN_ALERT_ORIGIN_IMPORTANCE =
  ' Pewne powody z kartoteki TMS (nieobecność ' +
  'kierowcy, pojazd poza eksploatacją, niedostępny lub w serwisie w realizacji) są pilne; ' +
  'szacunki są zwinięte ' +
  'jako podpowiedzi.';

/**
 * Dymek „Skąd ten alert?” — reguła: `apps/api/src/user-offers/replan-alerts.ts`.
 * Zdanie o pilnych i zwiniętych tylko z wyróżnieniem ważnych alertów.
 */
export function replanAlertOrigin(importantOn: boolean): string {
  return REPLAN_ALERT_ORIGIN_BASE + (importantOn ? REPLAN_ALERT_ORIGIN_IMPORTANCE : '');
}

/** Dymek z wyróżnieniem ważnych (develop) — karta pojazdu i testy. */
export const REPLAN_ALERT_ORIGIN = replanAlertOrigin(true);

/** Nagłówek karty z ważnym alertem (pewny powód z kartoteki). */
export const REPLAN_IMPORTANT_TITLE = 'Pilne przeplanowanie';
/** Nagłówek karty z samymi podpowiedziami (szacunek). */
export const REPLAN_TITLE = 'Rekomendowane przeplanowanie';

/** Linia zwiniętych podpowiedzi — „Inne…”, gdy nad nią stoi pilny alert. */
export function replanCollapsedLabel(count: number, belowImportant: boolean): string {
  return `${belowImportant ? 'Inne podpowiedzi' : 'Podpowiedzi'} przeplanowania (${count})`;
}

/** Stopka — jedyne miejsce, które mówi, że decyzja należy do człowieka. */
export const REPLAN_ALERT_NOTE = 'System niczego nie zmienił. O przeplanowaniu decydujesz Ty.';

/**
 * Końcówka uzasadnienia we wpisach sprzed 02.10.2026. Powtarzała przy każdym
 * alercie to, co mówi stopka. API już jej nie dopisuje, ale dziennika nie
 * przepisujemy — wycinamy ją przy wyświetlaniu.
 */
const LEGACY_REASON_ENDING = /\.?\s*Nic nie zmieniono — czy przeplanować, decyduje planista\.?\s*$/;

/** „Szacowana zajętość — oszacowanie” z tych samych wpisów: teraz „szacunek”, jak w API. */
const LEGACY_OCCUPANCY_HEDGE = /\(szacowana zajętość — oszacowanie\)/g;

/**
 * Stare uzasadnienia (wpisy sprzed poprawy brzmienia API, 01.10.2026) —
 * zapis małą literą, części po „; ”. Każdą część zamieniamy na zdanie:
 *
 * - zajętość: „pojazd zajęty w oknie zlecenia: inne obsadzenie zajmuje go…”
 *   → „Jego inne zlecenie nakłada się na ten termin (szacunek).”;
 * - kartoteka: „pojazd oznaczony jako niesprawny (kartoteka klienta — pewne)”
 *   → „Pojazd poza eksploatacją według kartoteki TMS (pewne).” — flaga
 *   `operational` z TMS nie mówi o usterce (07.10.2026, jak w API);
 * - lepsze auto: „wolne auto X: dolot … (krócej o …, N%; próg: …), zdąży…”
 *   → „Dolot krótszy o … (N%). Pojazd zdąży na załadunek…”;
 * - powtórzony początek „nie dowiezie przed końcem okna rozładunku: nie
 *   dowiezie na rozładunek: …” i „kierowca: kierowca …” — raz;
 * - „— oszacowanie (oszacowanie)” i pokrewne → „(szacunek)”.
 */
const LEGACY_BUSY =
  /^(?:pojazd|auto) zajęty w oknie zlecenia: inne obsadzenie zajmuje (?:go|je) w tym samym czasie\b/;
const LEGACY_VEHICLE_RECORD = /^(?:pojazd|auto) (.+?) \(kartoteka klienta — pewne\)$/;
/**
 * Flaga `operational` z TMS w starych wpisach: „oznaczony jako niesprawny”.
 * Od 07.10.2026 API mówi „poza eksploatacją”, jak rekomendacje i karta
 * pojazdu — flaga nie mówi o usterce. Dziennika nie przepisujemy, więc
 * stare wpisy pokazujemy nowym brzmieniem.
 */
const LEGACY_NOT_OPERATIONAL = 'oznaczony jako niesprawny';
/** To samo w zdaniach z API sprzed 07.10.2026: „Pojazd X jest oznaczony jako niesprawny.” */
const LEGACY_NOT_OPERATIONAL_SENTENCE = / jest oznaczony jako niesprawny\./g;
const LEGACY_BETTER_CAR =
  /^wolne auto .+?: dolot .+? zamiast .+? \(krócej o (.+?), (\d+)%; próg: [^)]*\), zdąży na załadunek, bez kandydatury do innego zlecenia \(dolot to szacunek ±8%\)$/;

function legacyEvidenceSentence(part: string): string {
  if (LEGACY_BUSY.test(part)) return 'Jego inne zlecenie nakłada się na ten termin (szacunek).';
  const record = LEGACY_VEHICLE_RECORD.exec(part);
  if (record) {
    return record[1] === LEGACY_NOT_OPERATIONAL
      ? 'Pojazd poza eksploatacją według kartoteki TMS (pewne).'
      : asSentence(`pojazd ${record[1]} w kartotece TMS (pewne)`);
  }
  const better = LEGACY_BETTER_CAR.exec(part);
  if (better) {
    return (
      `Dolot krótszy o ${better[1]} (${better[2]}%). Pojazd zdąży na załadunek ` +
      'i nie jest wskazany do innego zlecenia. Dolot to szacunek (±8%).'
    );
  }
  const text = part
    .replace(/^nie dowiezie przed końcem okna rozładunku: (?=nie dowiezie na rozładunek: )/, '')
    .replace(/^kierowca: (?=kierowca )/, '');
  return asSentence(hedgeAsEstimate(text));
}

/** Szara linia pod alertem: uzasadnienie bez powtórek; `null`, gdy nic nie zostaje. */
export function replanReasonText(reason: string | null): string | null {
  if (!reason) return null;
  const text = reason
    .replace(LEGACY_REASON_ENDING, '')
    .replace(LEGACY_OCCUPANCY_HEDGE, '(szacunek)')
    .trim();
  if (text === '') return null;
  // Nowe brzmienie (zdania od wielkiej litery) zostaje, jak przyszło.
  if (!startsLowercase(text)) return text;
  return splitOutsideParens(text).map(legacyEvidenceSentence).join(' ');
}

/** „obsadzony pojazd X: …” (wcześniej „obsadzone auto X: …”) — numer i reszta. */
const LEGACY_OWN = /^obsadzon(?:y pojazd|e auto)(?:\s+(.*?))?:\s+(.+)$/;
/** „wolne auto X ma dolot A zamiast B”. */
const LEGACY_BETTER_SUMMARY = /^wolne auto (.+?) ma dolot (.+)$/;

/** Jedno podsumowanie obsadzonego pojazdu jako zdanie (STYL-PL, 01.10.2026). */
function ownSentence(plate: string, summary: string): string {
  // Najstarsze wpisy: „auto zajęty w oknie zlecenia” (zły rodzaj, omówienie 01.10.2026).
  const s = summary.replace(/^auto /, '');
  const vehicle = plate ? `Pojazd ${plate}` : 'Pojazd';
  const ofVehicle = plate ? `pojazdu ${plate}` : 'pojazdu';
  switch (s) {
    case 'zajęty w oknie zlecenia':
      return `${vehicle} jest w tym czasie zajęty innym zleceniem.`;
    case LEGACY_NOT_OPERATIONAL:
      return `${vehicle} jest poza eksploatacją według kartoteki TMS.`;
    case 'oznaczony jako niedostępny':
      return `${vehicle} jest niedostępny według kartoteki TMS.`;
    case 'poza eksploatacją':
      return `${vehicle} jest poza eksploatacją.`;
    case 'kierowca nieobecny w dniu załadunku':
      return `Kierowca ${ofVehicle} jest nieobecny w dniu załadunku.`;
    case 'kierowca dostępny dopiero po załadunku':
      return `Kierowca ${ofVehicle} będzie dostępny dopiero po załadunku.`;
    default:
      if (/^nie (?:zdąży|dowiezie)\b/.test(s)) {
        return `${vehicle} ${s.replace(', nie dowiezie', ' i nie dowiezie')}.`;
      }
      return `${vehicle}: ${s}.`;
  }
}

/**
 * Wyraźna linia alertu. Stary zapis „obsadzony pojazd X: A; B; wolne auto
 * Y ma dolot …” → zdania „Pojazd X …. Wolny pojazd Y jest bliżej: dolot ….”.
 * Tekst w innym zapisie (nowe brzmienie z API) zostaje, jak przyszedł —
 * poza „jest oznaczony jako niesprawny” sprzed 07.10.2026.
 */
export function replanAlertText(text: string): string {
  if (!/^(?:obsadzon(?:y pojazd|e auto)|wolne auto)\b/.test(text)) {
    return text.replace(LEGACY_NOT_OPERATIONAL_SENTENCE, ' jest poza eksploatacją.');
  }
  // Kolejne części po „; ” dotyczą tego samego pojazdu, aż do „wolne auto”.
  let plate: string | null = null;
  return splitOutsideParens(text)
    .map((part) => {
      const own = LEGACY_OWN.exec(part);
      if (own) {
        plate = own[1] ?? '';
        return ownSentence(plate, own[2]);
      }
      const better = LEGACY_BETTER_SUMMARY.exec(part);
      if (better) {
        plate = null;
        return `Wolny pojazd ${better[1]} jest bliżej: dolot ${better[2]}.`;
      }
      return plate !== null ? ownSentence(plate, part) : asSentence(part);
    })
    .join(' ');
}

export const ReplanAlerts = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  // „Tylko master” i ważność to przełączniki funkcji (12.10.2026): bez
  // pierwszego kartę widzi każdy z planowaniem FTL (API i tak oddaje alerty
  // tylko jemu), bez drugiego — karta jak na produkcji.
  const hasAssign = useHasPermission('FTL_ASSIGN');
  const masterOnly = useFeature('replanAlertMasterOnly');
  const importantOn = useFeature('importantAlerts');
  const seesReplan = !masterOnly || hasAssign;
  const [alerts, setAlerts] = useState<TmsAlert[]>([]);
  const [busy, setBusy] = useState(false);
  const id = record?.id;

  const load = useCallback(async () => {
    if (id === undefined || id === null || !seesReplan) {
      setAlerts([]);
      return;
    }
    try {
      setAlerts((await fetchTmsAlerts(id)).filter((a) => a.kind === 'replan'));
    } catch {
      // Błąd odczytu chowa alert — karta działa dalej.
      setAlerts([]);
    }
  }, [id, seesReplan]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!seesReplan || alerts.length === 0 || id === undefined || id === null) return null;

  const acknowledge = async () => {
    setBusy(true);
    try {
      await acknowledgeTmsAlerts(
        id,
        alerts.map((a) => a.id),
      );
      notify('Odnotowano w historii zlecenia', { type: 'success' });
      await load();
      refresh();
    } catch (err) {
      notify(toErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const toLine = (a: TmsAlert) => ({
    id: a.id,
    text: replanAlertText(a.text),
    detail: replanReasonText(a.reason),
  });
  // Bez wyróżnienia ważnych (produkcja) — wszystkie alerty rozwinięte na
  // pomarańczowej karcie „Rekomendowane przeplanowanie”.
  const important = importantOn ? alerts.filter((a) => isImportantAlert(a)) : [];
  const normal = importantOn ? alerts.filter((a) => !isImportantAlert(a)) : [];

  return (
    <OrderAlertCard
      tone={!importantOn ? 'warning' : important.length > 0 ? 'error' : 'muted'}
      title={important.length > 0 ? REPLAN_IMPORTANT_TITLE : REPLAN_TITLE}
      detectedAt={alerts.map((a) => a.at)}
      origin={replanAlertOrigin(importantOn)}
      lines={importantOn ? important.map(toLine) : alerts.map(toLine)}
      collapsed={
        normal.length > 0
          ? { label: replanCollapsedLabel(normal.length, important.length > 0), lines: normal.map(toLine) }
          : undefined
      }
      note={REPLAN_ALERT_NOTE}
      actions={
        <>
          <Button
            variant="outlined"
            endIcon={<ArrowDownwardRoundedIcon />}
            sx={ALERT_BUTTON_SX}
            onClick={() => showVehicleSuggestions()}
          >
            Pokaż rekomendowane pojazdy
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={<CheckRoundedIcon />}
            sx={ALERT_BUTTON_SX}
            disabled={busy}
            onClick={() => void acknowledge()}
          >
            Przyjmuję do wiadomości
          </Button>
        </>
      }
    />
  );
};
